/* Amouve Kapok PDP — configurator, gallery, reviews, autoplay media.
   No dependencies. Safe to include more than once (guarded). */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Pure selection logic (no DOM) — unit-testable in Node.
  ------------------------------------------------------------------ */
  var AKLogic = {
    /** Variants matching height + dimension. Prefers available ones, then the
        one whose remaining option values match `prefer` (e.g. the legacy 3rd option). */
    findVariant: function (variants, hi, di, height, dim, prefer) {
      var matches = variants.filter(function (v) {
        return v.options[hi] === height && v.options[di] === dim;
      });
      if (!matches.length) return null;
      var score = function (v) {
        var s = v.available ? 100 : 0;
        if (prefer) {
          for (var i = 0; i < v.options.length; i++) {
            if (i !== hi && i !== di && v.options[i] === prefer[i]) s += 1;
          }
        }
        return s;
      };
      return matches.slice().sort(function (a, b) { return score(b) - score(a); })[0];
    },

    isAvailable: function (variants, hi, di, height, dim) {
      return variants.some(function (v) {
        return v.available && v.options[hi] === height && v.options[di] === dim;
      });
    },

    /** dims: [{value, bucket}] in display order. Returns first available value in bucket, or null. */
    firstAvailableDim: function (variants, hi, di, height, dims, bucket) {
      for (var i = 0; i < dims.length; i++) {
        var d = dims[i];
        if ((bucket == null || d.bucket === bucket) && AKLogic.isAvailable(variants, hi, di, height, d.value)) {
          return d;
        }
      }
      return null;
    },

    /** Resolve a consistent state after a change. Never leaves an unavailable combo selected if an alternative exists. */
    resolve: function (variants, hi, di, dims, state) {
      var next = { height: state.height, dim: state.dim, bucket: state.bucket, custom: !!state.custom };
      if (next.custom) return next;
      var current = dims.filter(function (d) { return d.value === next.dim; })[0];
      var dimFitsBucket = current && current.bucket === next.bucket;
      if (!dimFitsBucket || !AKLogic.isAvailable(variants, hi, di, next.height, next.dim)) {
        var pick = AKLogic.firstAvailableDim(variants, hi, di, next.height, dims, next.bucket) ||
                   AKLogic.firstAvailableDim(variants, hi, di, next.height, dims, null);
        if (pick) { next.dim = pick.value; next.bucket = pick.bucket; }
      }
      return next;
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = AKLogic;
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  window.AKLogic = AKLogic;
  if (window.__akPdpLoaded) return;
  window.__akPdpLoaded = true;

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var qs = function (root, sel) { return root.querySelector(sel); };
  var qsa = function (root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

  /* ------------------------------------------------------------------
     Gallery: scroll-snap track, arrows, thumbnails, swipe (native).
  ------------------------------------------------------------------ */
  if (!customElements.get('ak-gallery')) {
    customElements.define('ak-gallery', class extends HTMLElement {
      connectedCallback() {
        this.track = qs(this, '[data-ak-track]');
        if (!this.track) return;
        this.slides = qsa(this, '[data-ak-slide]');
        this.thumbs = qsa(this, '[data-ak-thumb]');
        this.index = 0;
        var self = this;
        var prev = qs(this, '[data-ak-prev]');
        var next = qs(this, '[data-ak-next]');
        if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
        if (next) next.addEventListener('click', function () { self.go(self.index + 1); });
        this.thumbs.forEach(function (t) {
          t.addEventListener('click', function () { self.go(parseInt(t.dataset.akThumb, 10)); });
        });
        var ticking = false;
        this.track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            if (self.programmatic) return;
            var w = self.track.clientWidth || 1;
            var i = Math.round(self.track.scrollLeft / w);
            if (i !== self.index) self.setActive(i);
          });
        }, { passive: true });
        this.track.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
        });
      }
      go(i) {
        var n = this.slides.length;
        if (!n) return;
        i = ((i % n) + n) % n;
        var self = this;
        this.programmatic = true;
        clearTimeout(this.scrollTimer);
        var release = function () { self.programmatic = false; };
        this.scrollTimer = setTimeout(release, reducedMotion ? 50 : 900);
        if ('onscrollend' in window) this.track.addEventListener('scrollend', release, { once: true });
        this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
        this.setActive(i);
      }
      goToMedia(mediaId) {
        if (!mediaId) return;
        var idx = this.slides.findIndex(function (s) { return s.dataset.mediaId === String(mediaId); });
        if (idx > -1) this.go(idx);
      }
      setActive(i) {
        var prevSlide = this.slides[this.index];
        this.index = i;
        if (prevSlide && prevSlide !== this.slides[i]) {
          qsa(prevSlide, 'video').forEach(function (v) { v.pause(); });
        }
        this.thumbs.forEach(function (t, ti) {
          if (ti === i) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Product configurator.
  ------------------------------------------------------------------ */
  if (!customElements.get('ak-product')) {
    customElements.define('ak-product', class extends HTMLElement {
      connectedCallback() {
        var dataEl = qs(this, '[data-ak-variants]');
        this.form = qs(this, '[data-ak-form]');
        this.select = qs(this, '[data-ak-variant-select]');
        if (!dataEl || !this.form || !this.select) return;
        try {
          this.variants = JSON.parse(dataEl.textContent);
        } catch (e) {
          // Leave the native select visible so the customer can still buy.
          this.classList.add('ak-js-failed');
          return;
        }
        this.hi = parseInt(this.dataset.heightIndex, 10) || 0;
        this.di = parseInt(this.dataset.dimIndex, 10) || 0;
        this.dimInputs = qsa(this, 'input[name="ak-dim"]');
        this.bedInputs = qsa(this, 'input[name="ak-bed"]');
        this.heightInputs = qsa(this, 'input[name="ak-height"]');
        this.dims = this.dimInputs.map(function (i) { return { value: i.value, bucket: i.dataset.bucket }; });
        this.pillow = qs(this, '[data-ak-pillow]');
        this.gallery = qs(this, '[data-ak-gallery]');
        this.buyButtons = qsa(this, '[data-ak-buy]');
        this.errorEl = qs(this, '[data-ak-error]');
        this.money = this.makeFormatter();

        var initial = this.variants.find(function (v) { return String(v.id) === this.select.value; }, this) ||
                      this.variants.find(function (v) { return v.available; }) || this.variants[0];
        this.prefer = initial.options.slice();
        this.lastMedia = initial.media;
        var dimEntry = this.dims.filter(function (d) { return d.value === initial.options[this.di]; }, this)[0];
        this.state = AKLogic.resolve(this.variants, this.hi, this.di, this.dims, {
          height: initial.options[this.hi],
          dim: initial.options[this.di],
          bucket: dimEntry ? dimEntry.bucket : 'single',
          custom: false
        });

        this.addEventListener('change', this.onChange.bind(this));
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        window.addEventListener('pageshow', this.resetBusy.bind(this)); // bfcache: returning from checkout
        this.render(false);
        this.initSticky();
      }

      makeFormatter() {
        var currency = this.dataset.currency || 'INR';
        var locale = currency === 'INR' ? 'en-IN' : (this.dataset.locale || 'en');
        return function (cents) {
          var whole = cents % 100 === 0;
          try {
            return new Intl.NumberFormat(locale, {
              style: 'currency', currency: currency,
              minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
            }).format(cents / 100);
          } catch (e) {
            return currency + ' ' + (cents / 100).toFixed(whole ? 0 : 2);
          }
        };
      }

      onChange(e) {
        var t = e.target;
        if (!t) return;
        if (t === this.pillow) { this.render(false); return; }
        var s = Object.assign({}, this.state);
        if (t.name === 'ak-height') s.height = t.value;
        else if (t.name === 'ak-bed') {
          if (t.value === 'custom') s.custom = true;
          else { s.custom = false; s.bucket = t.value; }
        } else if (t.name === 'ak-dim') s.dim = t.value;
        else return;
        this.state = AKLogic.resolve(this.variants, this.hi, this.di, this.dims, s);
        this.hideError();
        this.render(true);
      }

      currentVariant() {
        if (this.state.custom) return null;
        return AKLogic.findVariant(this.variants, this.hi, this.di, this.state.height, this.state.dim, this.prefer);
      }

      bucketLabel(bucket) {
        var input = this.bedInputs.filter(function (i) { return i.value === bucket; })[0];
        return input ? input.dataset.label : bucket;
      }

      render(userInitiated) {
        var s = this.state, self = this;
        var v = this.currentVariant();

        this.heightInputs.forEach(function (i) { i.checked = i.value === s.height; });

        this.bedInputs.forEach(function (i) {
          i.checked = s.custom ? i.value === 'custom' : i.value === s.bucket;
          if (i.value !== 'custom') {
            i.disabled = !self.dims.some(function (d) {
              return d.bucket === i.value && AKLogic.isAvailable(self.variants, self.hi, self.di, s.height, d.value);
            });
          }
        });

        this.dimInputs.forEach(function (i) {
          var show = !s.custom && i.dataset.bucket === s.bucket;
          var label = i.nextElementSibling;
          i.hidden = !show;
          if (label) label.hidden = !show;
          i.disabled = !AKLogic.isAvailable(self.variants, self.hi, self.di, s.height, i.value);
          i.checked = i.value === s.dim;
        });

        var dimGroup = qs(this, '[data-ak-dim-group]');
        var customNote = qs(this, '[data-ak-custom-note]');
        if (dimGroup) dimGroup.hidden = s.custom;
        if (customNote) customNote.hidden = !s.custom;

        if (v) this.select.value = String(v.id);

        var pillowOn = !!(this.pillow && this.pillow.checked && !s.custom);
        var pillowPrice = pillowOn ? parseInt(this.pillow.dataset.price, 10) || 0 : 0;
        var dimsText = s.custom ? 'Custom' : s.dim.toLowerCase().replace(/\s/g, '').replace('x', '×') + ' in';
        var total = v ? v.price + pillowPrice : null;

        this.out('height', s.height);
        this.out('bed', s.custom ? this.bucketLabel('custom') : this.bucketLabel(s.bucket));
        this.out('dims', dimsText);
        this.out('price', s.custom ? 'On request' : (v ? this.money(v.price) : '–'));
        this.out('total', s.custom ? 'On request' : (total != null ? this.money(total) : '–'));
        this.out('sticky-total', total != null ? this.money(total) : '');
        this.out('sticky-meta', s.height + ', ' + dimsText);
        var pillowLine = qs(this, '[data-ak-pillow-line]');
        if (pillowLine) pillowLine.hidden = !pillowOn;

        var canBuy = !!(v && v.available);
        this.buyButtons.forEach(function (b) {
          b.hidden = s.custom;
          b.disabled = !canBuy;
          if (!self.busy) b.textContent = canBuy ? (b.dataset.label || self.buyButtons[0].dataset.label || 'Buy') : 'Sold out';
        });
        var customCta = qs(this, '[data-ak-custom-cta]');
        if (customCta) customCta.hidden = !s.custom;

        this.updateWhatsApp(dimsText);

        if (userInitiated && v && this.dataset.updateUrl === 'true' && window.history && history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState(history.state, '', url.toString());
        }

        if (v && v.media && v.media !== this.lastMedia && this.gallery && this.gallery.goToMedia) {
          this.lastMedia = v.media;
          this.gallery.goToMedia(v.media);
        }

        if (this.sticky) this.updateSticky();
        this.dispatchEvent(new CustomEvent('ak:variant-change', { bubbles: true, detail: { variant: v, state: s } }));
      }

      out(key, text) {
        qsa(this, '[data-ak-out="' + key + '"]').forEach(function (el) { el.textContent = text; });
      }

      updateWhatsApp(dimsText) {
        var num = this.dataset.waNumber;
        if (!num) return;
        var s = this.state;
        var msg = s.custom
          ? 'Hi Amouve, I would like a custom-size ' + this.dataset.productTitle + ' (' + s.height + '). My bed-frame measurements are: '
          : 'Hi Amouve, I am interested in the ' + this.dataset.productTitle + ': ' + s.height + ', ' + this.bucketLabel(s.bucket) + ', ' + dimsText + '.';
        var href = 'https://wa.me/' + num + '?text=' + encodeURIComponent(msg);
        qsa(this, '[data-ak-wa], [data-ak-custom-cta]').forEach(function (a) { a.href = href; });
      }

      async onSubmit(e) {
        e.preventDefault();
        if (this.busy) return;
        var v = this.currentVariant();
        if (!v || !v.available) return;
        var items = [{ id: v.id, quantity: 1 }];
        if (this.pillow && this.pillow.checked) {
          items.push({ id: parseInt(this.pillow.dataset.variantId, 10), quantity: parseInt(this.pillow.dataset.quantity, 10) || 1 });
        }
        this.setBusy(true);
        try {
          var res = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: items })
          });
          var data = {};
          try { data = await res.json(); } catch (_) { /* non-JSON error page */ }
          if (!res.ok) {
            throw new Error(data.description || data.message || 'This size could not be added to your cart. Please try another size or order on WhatsApp.');
          }
          window.location.href = this.dataset.redirectUrl || '/cart';
        } catch (err) {
          var msg = err instanceof TypeError
            ? 'We could not reach the cart. Check your connection and try again, or order on WhatsApp.'
            : err.message;
          this.showError(msg);
          this.setBusy(false);
        }
      }

      setBusy(on) {
        this.busy = on;
        var self = this;
        this.buyButtons.forEach(function (b) {
          b.setAttribute('aria-busy', on ? 'true' : 'false');
          b.disabled = on;
          b.textContent = on ? 'Adding…' : (b.dataset.label || self.buyButtons[0].dataset.label || 'Buy');
        });
        if (!on) this.render(false);
      }

      resetBusy(e) { if (e.persisted && this.busy) this.setBusy(false); }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
      }

      initSticky() {
        this.sticky = qs(this, '[data-ak-sticky]');
        this.summaryEl = qs(this, '[data-ak-summary]');
        if (!this.sticky || !this.summaryEl) return;
        // Scroll check (not IntersectionObserver): IO misses instant jumps past the summary (anchor links, fast flicks).
        var self = this, ticking = false;
        var check = function () {
          ticking = false;
          self.pastSummary = self.summaryEl.getBoundingClientRect().bottom < 0;
          self.updateSticky();
        };
        window.addEventListener('scroll', function () {
          if (!ticking) { ticking = true; requestAnimationFrame(check); }
        }, { passive: true });
        check();
      }

      updateSticky() {
        var v = this.currentVariant();
        this.sticky.hidden = !(this.pastSummary && v && v.available && !this.state.custom);
      }
    });
  }

  /* ------------------------------------------------------------------
     Featured reviews: expand text, photo lightbox.
  ------------------------------------------------------------------ */
  if (!customElements.get('ak-reviews')) {
    customElements.define('ak-reviews', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        this.dialog = qs(this, 'dialog');
        this.addEventListener('click', function (e) {
          var toggle = e.target.closest('[data-ak-expand]');
          if (toggle) {
            var body = document.getElementById(toggle.getAttribute('aria-controls'));
            var open = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
            toggle.textContent = open ? toggle.dataset.more : toggle.dataset.less;
            if (body) body.classList.toggle('is-expanded', !open);
            return;
          }
          var photo = e.target.closest('[data-ak-photo]');
          if (photo && self.dialog && typeof self.dialog.showModal === 'function') {
            var img = qs(self.dialog, 'img');
            img.src = photo.dataset.akPhoto;
            img.alt = photo.dataset.alt || '';
            self.dialog.showModal();
            return;
          }
          if (e.target === self.dialog || e.target.closest('[data-ak-close]')) self.dialog.close();
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Autoplay background media: pause off-screen, respect reduced motion.
  ------------------------------------------------------------------ */
  function initAutoplay() {
    var vids = qsa(document, '[data-ak-autoplay] video');
    if (!vids.length) return;
    vids.forEach(function (v) {
      v.muted = true;
      v.setAttribute('playsinline', '');
      if (reducedMotion) { v.removeAttribute('autoplay'); v.pause(); v.controls = true; }
    });
    if (reducedMotion || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { var p = en.target.play(); if (p && p.catch) p.catch(function () {}); }
        else en.target.pause();
      });
    }, { threshold: 0.25 });
    vids.forEach(function (v) { io.observe(v); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAutoplay);
  else initAutoplay();
})();
